import { Injectable, signal } from '@angular/core';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class GoogleMapsLoaderService {
  private readonly apiKey = environment.googleMapsApiKey;
  private scriptLoadingPromise: Promise<void> | null = null;
  private geocodingLibPromise: Promise<google.maps.GeocodingLibrary> | null =
    null;
  public mapLoaded = signal(false);

  load(): Promise<void> {
    if (this.scriptLoadingPromise !== null) {
      return this.scriptLoadingPromise;
    }

    this.scriptLoadingPromise = new Promise((resolve, reject) => {
      if ((window as any).google?.maps) {
        this.mapLoaded.set(true);
        resolve();
        return;
      }

      const script = document.createElement('script');
      // Libraries (places, geocoding, …) are loaded on demand via
      // google.maps.importLibrary(...) — see WHP-12496.
      // Stable channel: weekly 3.66 intermittently throws inside the Map
      // constructor ("IntersectionObserver.observe ... not of type 'Element'").
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&callback=initMap&loading=async&v=quarterly`;
      script.async = true;
      script.defer = true;

      (window as any).initMap = () => {
        this.mapLoaded.set(true);
        resolve();
      };

      script.onerror = () => {
        this.mapLoaded.set(false);
        // reset the cache so a later screen can retry instead of being stuck
        // with a promise that already rejected
        this.scriptLoadingPromise = null;
        reject(
          new Error(
            `Failed to load Google Maps script (${script.src.split('?')[0]})`,
          ),
        );
      };

      document.head.appendChild(script);
    });

    return this.scriptLoadingPromise;
  }

  // Lazy-loads and caches the `geocoding` library so callers can construct
  // `new google.maps.Geocoder()` safely under async modular loading.
  loadGeocoding(): Promise<google.maps.GeocodingLibrary> {
    if (this.geocodingLibPromise !== null) {
      return this.geocodingLibPromise;
    }
    this.geocodingLibPromise = this.load()
      .then(
        () =>
          google.maps.importLibrary(
            'geocoding',
          ) as Promise<google.maps.GeocodingLibrary>,
      )
      .catch((error) => {
        this.geocodingLibPromise = null;
        throw error;
      });
    return this.geocodingLibPromise;
  }
}
